import React, { useState, useEffect, useRef } from "react";
import Modal from "../Modal/Modal";
import { showToast } from "../Toaster";
import { useLoader } from "../../contexts/LoaderContext";
import DriverService from "../../services/DriverService";
import { useAuth } from "../../contexts/AuthContext";
import CustomSelect, { CustomSelectRef } from "../CoreComponents/CustomSelect";
import PaginatedSearchSelect, {
  PaginatedSearchSelectRef,
} from "../CoreComponents/PaginatedSearchSelect";
import {
  CreateDriverRequest,
  UpdateDriverRequest,
} from "../../interfaces/driver.interface";
import TruckService from "../../services/Truck.service";
import TransporterService from "../../services/TransporterService";
import { rbacUtils } from "../../utils/rbacUtils";

interface DriverFormProps {
  isOpen: boolean;
  onClose: () => void;
  driver?: any;
  onSuccess: () => void;
  readOnly?: boolean;
}

const DriverForm = ({
  isOpen,
  onClose,
  driver,
  onSuccess,
  readOnly = false,
}: DriverFormProps) => {
  const { showLoader, hideLoader } = useLoader();
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState<CreateDriverRequest>({
    driverName: "",
    transporterId: null,
    driverLicenseNumber: "",
    driverMobileNumber: "",
    driverEmail: "",
    dateOfBirth: Date.now(), // store as timestamp
    driverAddress: "",
    truckId: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown options
  const [transporterOptions, setTransporterOptions] = useState<any[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [initialTruckOption, setInitialTruckOption] = useState<{
    value: string;
    label: string;
  } | null>(null);

  // Refs for CustomSelects
  const transporterSelectRef = useRef<CustomSelectRef>(null);
  const truckSelectRef = useRef<PaginatedSearchSelectRef>(null);

  const isEditMode = !!driver;

  useEffect(() => {
    if (driver) {
      // Prefer transporterId from driver, fallback to driver.transporterIdName?.id
      const transporterId =
        driver.transporterId ?? driver.transporterIdName?.id ?? null;
      const truckId = driver.truckId ?? driver.currentTruckIdName?.id ?? null;

      // Get truck name from driver object
      const truckName =
        driver.currentTruckIdName?.registrationNumber ||
        driver.currentTruckIdName?.name ||
        (truckId ? `Truck ${truckId}` : null);

      setFormData({
        driverName: driver.driverName || "",
        transporterId: transporterId,
        driverLicenseNumber: driver.driverLicenseNumber || "",
        driverMobileNumber: driver.driverMobileNumber || "",
        driverEmail: driver.driverEmail || "",
        dateOfBirth: driver.dateOfBirth
          ? Number(driver.dateOfBirth)
          : Date.now(),
        driverAddress: driver.driverAddress || "",
        truckId: truckId,
      });

      // Set initial truck option if truck exists
      if (truckId && truckName) {
        setInitialTruckOption({
          value: String(truckId),
          label: truckName,
        });
      } else {
        setInitialTruckOption(null);
      }
    } else {
      setFormData({
        driverName: "",
        transporterId: null,
        driverLicenseNumber: "",
        driverMobileNumber: "",
        driverEmail: "",
        dateOfBirth: Date.now(),
        driverAddress: "",
        truckId: null,
      });
      setInitialTruckOption(null);
    }
    setErrors({});
  }, [driver, userProfile]);
  // When formData.truckId changes, update truck select value
  useEffect(() => {
    if (
      truckSelectRef.current &&
      formData.truckId !== null &&
      formData.truckId !== undefined
    ) {
      truckSelectRef.current.setSeletedOption(String(formData.truckId));
    }
  }, [formData.truckId]);

  // When transporterOptions or formData.transporterId changes, update CustomSelect value
  useEffect(() => {
    if (
      transporterSelectRef.current &&
      transporterOptions.length > 0 &&
      formData.transporterId !== null &&
      formData.transporterId !== undefined
    ) {
      transporterSelectRef.current.setSeletedOption(
        String(formData.transporterId)
      );
    }
  }, [transporterOptions, formData.transporterId]);

  // Fetch transporter options on mount
  useEffect(() => {
    async function fetchOptions() {
      setLoadingOptions(true);
      try {
        // Check if user profile exists
        if (!userProfile) {
          setTransporterOptions([]);
          setLoadingOptions(false);
          return;
        }

        // Check if user is ROLE_TRANSPORTER_ADMIN
        const isTransporterAdmin = rbacUtils.isTransporterAdmin(userProfile);

        if (isTransporterAdmin) {
          // For ROLE_TRANSPORTER_ADMIN: Show only self transporter
          if (userProfile?.org?.id && userProfile?.org?.name) {
            const selfTransporter = {
              id: userProfile.org.id,
              name: userProfile.org.name,
            };
            setTransporterOptions([selfTransporter]);

            // Auto-select self transporter if not in edit mode
            if (!driver) {
              setFormData((prev) => ({
                ...prev,
                transporterId: selfTransporter.id,
              }));
            }
          } else {
            setTransporterOptions([]);
          }
        } else {
          // For other roles (ROLE_CLIENT_ADMIN, ROLE_FEDERATION_ADMIN): Show all transporters
          const transporterRes = await TransporterService.getTransporters(
            userProfile
          );
          let transporters = [];
          if (Array.isArray(transporterRes?.data?.data)) {
            transporters = transporterRes.data.data;
          } else if (transporterRes?.data?.data?.content) {
            transporters = transporterRes.data.data.content;
          } else if (Array.isArray(transporterRes?.data)) {
            transporters = transporterRes.data;
          }
          setTransporterOptions(transporters);
        }
      } catch (err) {
        console.error("Error fetching transporters:", err);
        setTransporterOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    }
    fetchOptions();
  }, [driver, userProfile]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.driverName.trim()) newErrors.driverName = "Name is required";
    if (!formData.driverLicenseNumber.trim())
      newErrors.driverLicenseNumber = "License number is required";
    if (!formData.driverMobileNumber.trim())
      newErrors.driverMobileNumber = "Mobile number is required";
    if (!formData.driverAddress.trim())
      newErrors.driverAddress = "Address is required";
    if (!formData.dateOfBirth)
      newErrors.dateOfBirth = "Date of birth is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "dateOfBirth") {
      // Convert date string to timestamp
      const timestamp = new Date(value).getTime();
      setFormData((prev) => ({ ...prev, [name]: timestamp }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle CustomSelect changes for transporter and truck
  const handleTransporterChange = (selected: string | string[]) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    setFormData((prev) => ({
      ...prev,
      transporterId: value ? Number(value) : null,
      truckId: null, // Reset truck selection when transporter changes
    }));

    // Reset truck select when transporter changes
    if (truckSelectRef.current) {
      truckSelectRef.current.reset();
    }

    if (errors.transporterId) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.transporterId;
        return newErrors;
      });
    }
  };

  const handleTruckChange = (selected: string) => {
    setFormData((prev) => ({
      ...prev,
      truckId: selected ? Number(selected) : null,
    }));
    if (errors.truckId) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.truckId;
        return newErrors;
      });
    }
  };

  // Load trucks for paginated search select
  const loadTrucks = async (search: string, page: number) => {
    try {
      const result = await TruckService.getTrucksPaginated(
        search,
        page,
        20,
        userProfile,
        formData.transporterId ? { transporterId: formData.transporterId } : {}
      );

      return {
        options: result.trucks.map((truck) => ({
          value: String(truck.id),
          label: truck.registrationNumber || truck.name || `Truck ${truck.id}`,
        })),
        hasMore: result.hasMore,
        total: result.total,
      };
    } catch (error) {
      console.error("Error loading trucks:", error);
      return {
        options: [],
        hasMore: false,
        total: 0,
      };
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setIsSubmitting(true);
      showLoader();
      if (isEditMode) {
        const updateData: UpdateDriverRequest = {
          ...formData,
          id: driver!.id!,
        };
        await DriverService.updateDriverRBAC(userProfile, updateData);
        showToast("Driver updated successfully", "success");
      } else {
        await DriverService.createDriverRBAC(userProfile, formData);
        showToast("Driver created successfully", "success");
      }
      console.log("Form data to submit:", formData);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving driver:", error);
      showToast(
        error.response?.data?.message || "Failed to save driver",
        "error"
      );
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };
  // Helper to format timestamp for input type="date"
  const formatDateForInput = (timestamp: number) => {
    if (!timestamp) return "";
    return new Date(timestamp).toISOString().split("T")[0];
  };

  const getFooterButtons = () => (
    <div className="flex justify-center gap-4">
      <button
        type="button"
        className="secondary_button w-[128px]"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      {!readOnly && (
        <button
          type="button"
          className="primary_button w-[128px]"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
        </button>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Driver" : "Add Driver"}
      size="xl"
      closeOnModalOverlayClick={true}
      footer={getFooterButtons()}
    >
      <div className="modal-form">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="form-group lg:col-span-2">
            <label className="form-label required_field_label">
              Driver Name
            </label>
            <input
              type="text"
              name="driverName"
              value={formData.driverName}
              onChange={handleInputChange}
              placeholder="Enter driver name"
              className={`form-input ${errors.driverName ? "error" : ""}`}
              disabled={readOnly}
            />
            {errors.driverName && (
              <div className="form-help error">{errors.driverName}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label required_field_label">
              Transporter
            </label>
            <CustomSelect
              ref={transporterSelectRef}
              label="Transporter"
              options={transporterOptions.map((t) => ({
                value: String(t.id),
                label: t.name,
              }))}
              placeholder="Select transporter"
              onChange={handleTransporterChange}
              className="form-input"
              disabled={readOnly || loadingOptions}
              error={!!errors.transporterId}
            />
            {errors.transporterId && (
              <div className="form-help error">{errors.transporterId}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Truck</label>
            <PaginatedSearchSelect
              ref={truckSelectRef}
              label="Truck"
              placeholder="Select truck"
              onChange={handleTruckChange}
              onLoadMore={loadTrucks}
              className="form-input"
              disabled={readOnly}
              error={!!errors.truckId}
              pageSize={20}
              searchDebounceMs={300}
              initialOption={initialTruckOption}
            />
            {errors.truckId && (
              <div className="form-help error">{errors.truckId}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label required_field_label">
              License Number
            </label>
            <input
              type="text"
              name="driverLicenseNumber"
              value={formData.driverLicenseNumber}
              onChange={handleInputChange}
              placeholder="Enter license number"
              className={`form-input ${
                errors.driverLicenseNumber ? "error" : ""
              }`}
              disabled={readOnly}
            />
            {errors.driverLicenseNumber && (
              <div className="form-help error">
                {errors.driverLicenseNumber}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label required_field_label">
              Mobile Number
            </label>
            <input
              type="text"
              name="driverMobileNumber"
              value={formData.driverMobileNumber}
              onChange={handleInputChange}
              placeholder="Enter mobile number"
              className={`form-input ${
                errors.driverMobileNumber ? "error" : ""
              }`}
              disabled={readOnly}
            />
            {errors.driverMobileNumber && (
              <div className="form-help error">{errors.driverMobileNumber}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="driverEmail"
              value={formData.driverEmail || ""}
              onChange={handleInputChange}
              placeholder="Enter email (optional)"
              className="form-input"
              disabled={readOnly}
            />
          </div>
          <div className="form-group">
            <label className="form-label required_field_label">
              Date of Birth
            </label>
            <input
              type="date"
              name="dateOfBirth"
              value={formatDateForInput(formData.dateOfBirth)}
              onChange={handleInputChange}
              className={`form-input ${errors.dateOfBirth ? "error" : ""}`}
              pattern="\d{4}-\d{2}-\d{2}"
              placeholder="YYYY-MM-DD"
              disabled={readOnly}
            />
            {errors.dateOfBirth && (
              <div className="form-help error">{errors.dateOfBirth}</div>
            )}
          </div>
          <div className="form-group lg:col-span-2">
            <label className="form-label required_field_label">Address</label>
            <input
              type="text"
              name="driverAddress"
              value={formData.driverAddress}
              onChange={handleInputChange}
              placeholder="Enter address"
              className={`form-input ${errors.driverAddress ? "error" : ""}`}
              disabled={readOnly}
            />
            {errors.driverAddress && (
              <div className="form-help error">{errors.driverAddress}</div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DriverForm;
